import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Work } from '@/mocks/works';

interface AnimeCardProps {
  work: Work;
  rank?: number;
}

const statusMap = {
  airing: { label: 'tag_airing', cls: 'bg-accent-100 text-accent-700' },
  finished: { label: 'tag_finished', cls: 'bg-secondary-100 text-secondary-900' },
  not_start: { label: 'tag_not_start', cls: 'bg-background-200 text-foreground-600' },
} as const;

export default function AnimeCard({ work, rank }: AnimeCardProps) {
  const { t } = useTranslation();
  const status = statusMap[work.status];

  return (
    <Link
      to={`/category/${work.categories[0]}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition hover:border-primary-300"
      aria-label={`${work.title} - ${work.titleEn}`}
    >
      {/* Cover */}
      <div className="relative aspect-[3/4] overflow-hidden bg-background-100">
        <img
          src={work.cover}
          alt={work.title}
          title={`${work.title} 动漫网动画库`}
          className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
        />
        {/* rank badge */}
        {rank !== undefined && (
          <span className="absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-md bg-primary-500 text-xs font-bold text-background-50">
            {rank}
          </span>
        )}
        {/* status badge */}
        <span className={`absolute right-2 top-2 rounded-full px-2 py-0.5 text-[11px] font-medium ${status.cls}`}>
          {t(status.label)}
        </span>
        {/* rating */}
        <span className="absolute bottom-2 right-2 rounded-md bg-foreground-950/70 px-2 py-0.5 text-xs font-semibold text-background-50">
          <i className="ri-star-fill mr-0.5 text-secondary-400" aria-hidden="true"></i>
          {work.rating.toFixed(1)}
        </span>
        {/* hover overlay */}
        <div className="absolute inset-0 flex items-end bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent p-3 opacity-0 transition group-hover:opacity-100">
          <span className="text-sm font-medium text-background-50">{t('btn_detail')}</span>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-3">
        <h3 className="truncate text-sm font-semibold text-foreground-950 group-hover:text-primary-600">
          {work.title}
        </h3>
        <p className="mt-0.5 truncate text-xs text-foreground-600">{work.titleEn}</p>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {work.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="rounded bg-secondary-100 px-1.5 py-0.5 text-[11px] text-secondary-900"
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="mt-2 flex items-center justify-between border-t border-background-200/70 pt-2 text-xs text-foreground-600">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line" aria-hidden="true"></i>
            {work.year}
          </span>
          <span className="truncate">{work.episodes}</span>
        </div>
      </div>
    </Link>
  );
}